import React, { useState } from 'react';
import { PageContainer } from '@ant-design/pro-components';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Input,
    Row,
    Space,
    Spin,
    Statistic,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    InstagramOutlined,
    LinkOutlined,
    SearchOutlined,
} from '@ant-design/icons';
// import Icon from '@ant-design/icons/lib/components/Icon';
// import { ReactComponent as TikTokSvg } from '../../assets/tiktok.svg';
import dayjs from 'dayjs';
import axiosApiInstance from '../../api/axiosClient';
import { ApiResponse } from './types';

const { Text, Paragraph } = Typography;

export interface PostMetricsData {
    post_id: string;
    post_url: string;
    post_type: string;
    creator_username: string;
    caption?: string;
    description?: string;
    created_at: string;
    likes: number;
    comments: number;
    views: number;
    shares: number;
    saves?: number;
    engagement_rate: number;
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value);
}

function formatPercent(value: number): string {
    return `${(value * 100).toFixed(2)}%`;
}

interface PostMetricsCardProps {
    platform: 'instagram' | 'tiktok';
    apiPath: string;
    loadingTip: string;
    urlPlaceholder: string;
    openLabel: string;
    platformIcon: React.ReactNode;
    platformColor: string;
    emptyHint: string;
    inputWarning: string;
}

const PostMetricsCard: React.FC<PostMetricsCardProps> = ({
    platform,
    apiPath,
    loadingTip,
    urlPlaceholder,
    openLabel,
    platformIcon,
    platformColor,
    emptyHint,
    inputWarning,
}) => {
    const [postUrl, setPostUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState<PostMetricsData | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleLookup = async () => {
        const url = postUrl.trim();
        if (!url) {
            message.warning(inputWarning);
            return;
        }

        setLoading(true);
        setError(null);
        setMetrics(null);

        try {
            const response = await axiosApiInstance.get<ApiResponse<PostMetricsData>>(
                apiPath,
                { params: { url }, timeout: platform === 'tiktok' ? 120000 : 60000 },
            );
            if (response.data.success) {
                setMetrics(response.data.data);
            } else {
                setError(response.data.message || 'Failed to fetch post metrics');
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to fetch post metrics';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const description = metrics?.caption || metrics?.description;

    return (
        <Card
            title={(
                <Space>
                    {platformIcon}
                    <span>{platform === 'instagram' ? 'Instagram' : 'TikTok'} post lookup</span>
                </Space>
            )}
            style={{ height: '100%' }}
        >
            <Paragraph type="secondary">
                {platform === 'instagram'
                    ? 'Paste a public Instagram post or reel link to fetch live likes, comments, views, and shares.'
                    : 'Paste a public TikTok video or photo post link. Fetching may take up to a minute via BrightData.'}
            </Paragraph>

            {platform === 'tiktok' && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="BrightData lookup"
                    description="Use a full tiktok.com/@username/video/... URL. Short vm.tiktok.com links are not supported."
                />
            )}

            <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Input
                    size="large"
                    prefix={<LinkOutlined />}
                    placeholder={urlPlaceholder}
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    onPressEnter={handleLookup}
                    allowClear
                />
                <Button
                    type="primary"
                    size="large"
                    icon={<SearchOutlined />}
                    loading={loading}
                    onClick={handleLookup}
                >
                    Get metrics
                </Button>
            </Space.Compact>

            {loading && (
                <div style={{ textAlign: 'center', padding: 32 }}>
                    <Spin size="large" tip={loadingTip} />
                </div>
            )}

            {!loading && error && (
                <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
            )}

            {!loading && metrics && (
                <div>
                    <Space wrap style={{ marginBottom: 16 }}>
                        <Tag icon={platformIcon} color={platformColor}>
                            @{metrics.creator_username}
                        </Tag>
                        <Tag>{metrics.post_type}</Tag>
                        <a href={metrics.post_url} target="_blank" rel="noopener noreferrer">
                            {openLabel}
                        </a>
                    </Space>

                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic title="Likes" value={metrics.likes} formatter={(v) => formatNumber(Number(v))} />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic title="Comments" value={metrics.comments} formatter={(v) => formatNumber(Number(v))} />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic title="Views" value={metrics.views} formatter={(v) => formatNumber(Number(v))} />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic title="Shares" value={metrics.shares} formatter={(v) => formatNumber(Number(v))} />
                        </Col>
                        {metrics.saves != null && metrics.saves > 0 && (
                            <Col xs={12} sm={8} md={6}>
                                <Statistic title="Saves" value={metrics.saves} formatter={(v) => formatNumber(Number(v))} />
                            </Col>
                        )}
                        <Col xs={12} sm={8} md={6}>
                            <Statistic title="Engagement rate" value={formatPercent(metrics.engagement_rate)} />
                        </Col>
                    </Row>

                    <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="Posted">
                            {dayjs(metrics.created_at).isValid()
                                ? dayjs(metrics.created_at).format('DD MMM YYYY, HH:mm')
                                : metrics.created_at}
                        </Descriptions.Item>
                        {description && (
                            <Descriptions.Item label="Description">
                                <Text style={{ whiteSpace: 'pre-wrap' }}>{description}</Text>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </div>
            )}

            {!loading && !metrics && !error && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyHint} />
            )}
        </Card>
    );
};

const PostsMetricsPage: React.FC = () => (
    <PageContainer
        header={{
            title: 'Instagram Post Metrics',
            subTitle: 'Look up live engagement for a specific post or reel link',
        }}
    >
        <Row gutter={[24, 24]}>
            <Col xs={24} xl={16}>
                <PostMetricsCard
                    platform="instagram"
                    apiPath="/api/instagram-influencers/metrics/post"
                    loadingTip="Fetching live metrics from Instagram..."
                    urlPlaceholder="https://www.instagram.com/p/... or /reel/..."
                    openLabel="Open on Instagram"
                    platformIcon={<InstagramOutlined />}
                    platformColor="magenta"
                    emptyHint="Enter an Instagram post URL above to see metrics"
                    inputWarning="Please paste an Instagram post or reel URL"
                />
            </Col>
            {/* TikTok post lookup disabled for now
            <Col xs={24} xl={12}>
                <PostMetricsCard
                    platform="tiktok"
                    apiPath="/api/tiktok-brightdata/metrics/post"
                    loadingTip="Fetching metrics from TikTok via BrightData (this can take up to a minute)..."
                    urlPlaceholder="https://www.tiktok.com/@user/video/..."
                    openLabel="Open on TikTok"
                    platformIcon={<Icon component={TikTokSvg} />}
                    platformColor="default"
                    emptyHint="Enter a TikTok post URL above to see metrics"
                    inputWarning="Please paste a TikTok post URL"
                />
            </Col>
            */}
        </Row>
    </PageContainer>
);

export default PostsMetricsPage;
